<template>
  <!--
    v-show, NOT v-if. This was a real-world performance call.

    v-if destroys the entire component subtree on close: Vuetify children
    unmount, the rAF positioning loop's anchor reads stale state, watchers
    re-bind on reopen, the browser reflows when the host element disappears.
    The user observed this as a perceptible lag on close compared to
    IconThumbnailPopup (`src/components/common/thumbnailPopup/iconThumbnailPopup.vue:8`),
    which uses an inline `display: none` binding for the same reason — full
    component teardown is too expensive for a popup that opens/closes
    frequently.

    With v-show the component stays mounted forever (one extra Vue instance
    at the layout level, same cost as IconThumbnailPopup already pays). Open
    and close are CSS-only toggles, no DOM ops, no lifecycle churn.

    The watcher on `isOpen` (below) still fires on both directions because
    Vue.observable reactivity is independent of component mount state, so
    listener install/remove still happens correctly.
  -->
  <div
    v-show="isOpen"
    ref="hostEl"
    class="cml-popup-card-host"
    @click.stop
  >
    <div class="cml-popup-card cml-popup-card--v2">
      <!-- Loading skeleton — preserves the production-tile shape so position
           doesn't jump when data arrives -->
      <div v-if="status === 'Loading'" class="cml-popup-card__skel" data-test="popup-skeleton">
        <div class="cml-popup-card__skel-head">
          <div class="cml-popup-card__skel-dot"></div>
          <div class="cml-popup-card__skel-head-stack">
            <div class="cml-popup-card__skel-line cml-popup-card__skel-line--id"></div>
            <div class="cml-popup-card__skel-line cml-popup-card__skel-line--sub"></div>
          </div>
        </div>
        <div class="cml-popup-card__skel-hero">
          <div class="cml-popup-card__skel-line cml-popup-card__skel-line--hero"></div>
          <div class="cml-popup-card__skel-line cml-popup-card__skel-line--small"></div>
        </div>
        <div class="cml-popup-card__skel-facts">
          <div class="cml-popup-card__skel-row"></div>
          <div class="cml-popup-card__skel-row"></div>
          <div class="cml-popup-card__skel-row"></div>
          <div class="cml-popup-card__skel-row"></div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="status === 'Error'" class="cml-popup-card__error-state" data-test="popup-error">
        <div class="cml-popup-card__error-text">
          {{ error || 'Failed to load — please try again.' }}
        </div>
        <button class="cml-popup-card__action cml-popup-card__action--primary" @click="onRetry">Retry</button>
      </div>

      <!-- Loaded — CML variant -->
      <template v-else-if="status === 'Loaded' && vm && entityType === 'cml'">
        <div class="cml-popup-card__head" data-test="popup-header">
          <span
            class="cml-popup-card__dot"
            :style="dotStyle"
            :title="statusLabel"
            data-test="popup-status-dot"
          ></span>
          <div class="cml-popup-card__head-text">
            <div class="cml-popup-card__id-row">
              <span class="cml-popup-card__id" data-test="popup-cml-no">{{ vm.cmlNo }}</span>
              <span
                class="cml-popup-card__status"
                :style="{ backgroundColor: statusColor }"
                data-test="popup-status-pill"
              >{{ statusLabel }}</span>
            </div>
            <div class="cml-popup-card__sub" :title="subtitleLine">{{ subtitleLine }}</div>
          </div>
        </div>

        <div class="cml-popup-card__hero" data-test="popup-cml-body">
          <div class="cml-popup-card__hero-row">
            <span
              class="cml-popup-card__hero-value"
              :style="{ color: statusColor }"
              data-test="popup-cml-worst-cr"
            >{{ worstCrValue }}</span>
            <span class="cml-popup-card__hero-unit">mm per year</span>
          </div>
          <div class="cml-popup-card__hero-label">Worst Corrosion Rate</div>
        </div>

        <div class="cml-popup-card__facts">
          <div class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Remaining Life</span>
            <span
              class="cml-popup-card__fact-val"
              :class="{ 'cml-popup-card__fact-val--danger': isRemainingLifeCritical }"
              data-test="popup-cml-min-rl"
            >
              {{ remainingLifeDisplay }}
              <span v-if="remainingLifeAux" class="cml-popup-card__fact-val-aux">{{ remainingLifeAux }}</span>
            </span>
          </div>
          <div class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Current Minimum Wall Thickness</span>
            <span class="cml-popup-card__fact-val" data-test="popup-cml-current-min-wt">
              {{ vm.currentMinWtDisplay }}
              <span v-if="currentMinWtAux" class="cml-popup-card__fact-val-aux">{{ currentMinWtAux }}</span>
            </span>
          </div>
          <div v-if="designThicknessDisplay" class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Design Thickness</span>
            <span class="cml-popup-card__fact-val">{{ designThicknessDisplay }}</span>
          </div>
          <div class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Measurement Points</span>
            <span class="cml-popup-card__fact-val" data-test="popup-cml-tml-count">{{ measurementPointsDisplay }}</span>
          </div>
          <div class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Last Inspected</span>
            <span class="cml-popup-card__fact-val" data-test="popup-cml-last-inspection">
              {{ lastInspectedDisplay }}
              <span v-if="lastInspectedAux" class="cml-popup-card__fact-val-aux">{{ lastInspectedAux }}</span>
            </span>
          </div>
          <div v-if="nextInspectionDisplay" class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Next Inspection</span>
            <span class="cml-popup-card__fact-val">
              {{ nextInspectionDisplay }}
              <span v-if="nextInspectionAux" class="cml-popup-card__fact-val-aux">{{ nextInspectionAux }}</span>
            </span>
          </div>
        </div>

        <CmlPopupActionBar
          :vm="vm"
          :entity-type="entityType"
          :entity-id="entityId"
          :source-viewer="sourceViewer"
          @view-readings="onViewDetail"
          @create-task="onCreateTask"
          @link-task="onLinkTask"
        />
      </template>

      <!-- Loaded — TmlPoint variant (TRK-145) -->
      <template v-else-if="status === 'Loaded' && vm && entityType === 'tmlpoint'">
        <div class="cml-popup-card__head" data-test="popup-header">
          <span
            class="cml-popup-card__dot"
            :style="dotStyle"
            :title="statusLabel"
            data-test="popup-status-dot"
          ></span>
          <div class="cml-popup-card__head-text">
            <div class="cml-popup-card__id-row">
              <span class="cml-popup-card__id" data-test="popup-tml-position">{{ vm.positionLabel }}</span>
              <span
                class="cml-popup-card__status"
                :style="{ backgroundColor: statusColor }"
                data-test="popup-status-pill"
              >{{ statusLabel }}</span>
            </div>
            <div class="cml-popup-card__sub" :title="tmlSubtitleLine">{{ tmlSubtitleLine }}</div>
          </div>
        </div>

        <div class="cml-popup-card__hero" data-test="popup-tml-body">
          <div class="cml-popup-card__hero-row">
            <span
              class="cml-popup-card__hero-value"
              :style="{ color: statusColor }"
              data-test="popup-tml-latest-reading"
            >{{ tmlLatestReadingValue }}</span>
            <span class="cml-popup-card__hero-unit">mm</span>
          </div>
          <div class="cml-popup-card__hero-label">Latest Reading</div>
        </div>

        <div class="cml-popup-card__facts">
          <div class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Last Reading Date</span>
            <span class="cml-popup-card__fact-val" data-test="popup-tml-last-reading-date">
              {{ tmlLastReadingDateDisplay }}
              <span v-if="tmlLastReadingDateAux" class="cml-popup-card__fact-val-aux">{{ tmlLastReadingDateAux }}</span>
            </span>
          </div>
          <div class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Parent CML</span>
            <span class="cml-popup-card__fact-val" data-test="popup-tml-parent-cml">{{ tmlParentCmlLabel }}</span>
          </div>
          <div v-if="tmlParentMasterTagName" class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">Master Tag</span>
            <span class="cml-popup-card__fact-val">{{ tmlParentMasterTagName }}</span>
          </div>
          <div v-if="tmlForgeDbIdDisplay" class="cml-popup-card__fact">
            <span class="cml-popup-card__fact-key">3D Element</span>
            <span class="cml-popup-card__fact-val cml-popup-card__fact-val-aux">{{ tmlForgeDbIdDisplay }}</span>
          </div>
        </div>

        <!-- AVA-437 Tier 2 — the TmlPoint popup now uses the same action bar as the CML popup
             (documents = TML-linked, reading → Monitoring + highlight this TML point, show-in-
             other-viewer, add task). Replaces the old text-button footer. -->
        <CmlPopupActionBar
          :vm="vm"
          :entity-type="entityType"
          :entity-id="entityId"
          :source-viewer="sourceViewer"
          @view-readings="onViewDetail"
          @create-task="onCreateTask"
          @link-task="onLinkTask"
        />
      </template>
    </div>
  </div>
</template>

<script>
import {
  popupCardState,
  closePopupCard,
  retryPopupCard,
} from '@/composables/usePopupCard';
import { log as debugLog } from '@/utils/debugLogger';
import { statusColorForCml, statusLabelForCml } from '@/utils/cmlStatus';
import { POPUP_ACTION } from '@/components/cml/popupActions';
import CmlPopupActionBar from '@/components/cml/CmlPopupActionBar.vue';
import './popupCardStyles.scss';
import './productionTileStyles.scss';

/**
 * CmlPopupCard — singleton popup component (Wave C-α).
 *
 * Mounted once at the app shell (T011 mounts it in src/layouts/view.vue),
 * driven by the reactive `popupCardState` exported from
 * `@/composables/usePopupCard`. Both viewers (3D Forge + 360 Pannellum)
 * call `openPopupCard(...)` to populate this same instance — there is no
 * per-viewer mount (FR-012, FR-020 enforced by construction).
 *
 * Responsibilities:
 *   • Render the popup chrome when popupCardState.isOpen (FR-001, FR-021 non-modal).
 *   • Show skeleton placeholders during Loading (FR-022 / Clarification Q1).
 *   • Render an error + retry control during Error.
 *   • Render the action button row when Loaded, with v-if hiding per permission (FR-019 / Q4).
 *   • Handle Esc + click-outside close triggers (FR-014a, FR-014b).
 *   • Forward action-button clicks to parent emit handlers — US1 tasks T016/T018/T019
 *     wire the actual destinations (CmlEdit, Integrity Hub detail, Add Reading flow).
 *
 * Body content (cmlNo, cmlType, status pill, cached metrics, etc.) is filled
 * by US1's T017 via the named `<slot name="body">`. T007 leaves the slot
 * empty so the chrome can be demoed standalone during Foundational verification.
 */
export default {
  name: 'CmlPopupCard',

  components: { CmlPopupActionBar },

  data() {
    return {
      outsideHandler: null,
      escHandler: null,
    };
  },

  computed: {
    // Per CLAUDE.md "Vuex Store - Computed Properties for State Access" — we
    // wrap the shared reactive state in computed props so the template never
    // touches the imported object directly. This is the same discipline applied
    // to a Vue.observable singleton (not a Vuex store, but the same pattern).
    isOpen() { return popupCardState.isOpen; },
    status() { return popupCardState.status; },
    vm()     { return popupCardState.vm; },
    error()  { return popupCardState.error; },
    entityType() { return popupCardState.entityType; },
    entityId()   { return popupCardState.entityId; },
    sourceViewer() { return popupCardState.sourceViewer; },

    // Status LABEL delegates to the shared `cmlStatus` helper (SC-002). Status
    // COLOR now delegates to the SAME resolver the 3D/360 markers use
    // (forgeService._getCmlConditionColor), so the popup pill and the marker that
    // opened it never disagree — cmlStatus' Monitor (#FFEB3B) differed from the
    // markers' (#FBC02D). _statusValue() yields the enum name, which the resolver
    // accepts (it also normalizes int / numeric-string forms).
    statusColor() {
      const status = this._statusValue();
      const forgeService = this.$store.state.forgeService;
      if (forgeService && forgeService._getCmlConditionColor) {
        return forgeService._getCmlConditionColor({ conditionRating: status });
      }
      return statusColorForCml(status); // defensive fallback if forgeService is absent
    },
    statusLabel() {
      const status = this._statusValue();
      return statusLabelForCml(status);
    },
    dotStyle() {
      const c = this.statusColor;
      // Soft halo from the same OKLCH hue at high lightness — keeps the dot
      // legible against any background tint behind the popup.
      return {
        background: c,
        boxShadow: `0 0 0 4px ${c}26`, // ~15% alpha halo
      };
    },

    // Subtitle — type + parent master tag + ISO reference, separated by ·
    subtitleLine() {
      const r = (this.vm && this.vm.raw) || {};
      const parts = [];
      if (r.cmlType) parts.push(r.cmlType);
      const mt = r.masterTag && (r.masterTag.name || r.masterTag.tagNo);
      if (mt) parts.push(mt);
      if (r.isoReference) parts.push(r.isoReference);
      return parts.join(' · ') || (this.vm && this.vm.cmlType) || '—';
    },

    // Hero — worst corrosion rate as the unit-less value (unit shown next to it)
    worstCrValue() {
      const v = this.vm && this.vm.raw && this.vm.raw.worstCR_Cached;
      if (v == null || Number.isNaN(Number(v))) return '—';
      return Number(v).toFixed(3);
    },

    // Remaining Life — full-word display + weeks aux when < 1 year
    isRemainingLifeCritical() {
      const v = this.vm && this.vm.raw && this.vm.raw.minRL_Cached;
      return v != null && Number(v) < 1;
    },
    remainingLifeDisplay() {
      const v = this.vm && this.vm.raw && this.vm.raw.minRL_Cached;
      if (v == null || Number.isNaN(Number(v))) return '—';
      const n = Number(v);
      if (n >= 10) return `${n.toFixed(0)} years`;
      return `${n.toFixed(1)} years`;
    },
    remainingLifeAux() {
      const v = this.vm && this.vm.raw && this.vm.raw.minRL_Cached;
      if (v == null) return null;
      const n = Number(v);
      if (n >= 1) return null;
      const weeks = Math.max(1, Math.round(n * 52));
      return `~${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
    },

    // Current Min WT aux — "vs X.XX min"
    currentMinWtAux() {
      const mt = this.vm && this.vm.raw && this.vm.raw.minimumThickness;
      if (mt == null) return null;
      return `vs ${Number(mt).toFixed(2)} min`;
    },

    // Design Thickness — optional row (hidden when null)
    designThicknessDisplay() {
      const dt = this.vm && this.vm.raw && this.vm.raw.designThickness;
      if (dt == null) return null;
      return `${Number(dt).toFixed(2)} mm`;
    },

    // Measurement Points — plain count (vm.tmlPointsCountDisplay says "N TmlPoints")
    measurementPointsDisplay() {
      const n = this.vm && this.vm.raw && this.vm.raw.tmlPointsCount;
      if (n == null) return '—';
      return String(n);
    },

    // Last Inspected — relative + aux ISO
    lastInspectedDisplay() {
      const iso = this.vm && this.vm.raw && this.vm.raw.lastInspectionDate_Cached;
      if (!iso) return 'Never inspected';
      const rel = this._relativeTime(iso);
      return rel || this.vm.lastInspectionDisplay || '—';
    },
    lastInspectedAux() {
      const iso = this.vm && this.vm.raw && this.vm.raw.lastInspectionDate_Cached;
      if (!iso) return null;
      return this._formatDateShort(iso);
    },

    // Next Inspection — countdown + aux ISO (hidden when null)
    nextInspectionDisplay() {
      const iso = this.vm && this.vm.raw && this.vm.raw.nextInspectionDate;
      if (!iso) return null;
      return this._relativeTime(iso, { future: true });
    },
    nextInspectionAux() {
      const iso = this.vm && this.vm.raw && this.vm.raw.nextInspectionDate;
      if (!iso) return null;
      return this._formatDateShort(iso);
    },

    // ── TmlPoint variant computeds (TRK-145) ──────────────────────────────
    tmlSubtitleLine() {
      // "Measurement point on CR-005 · Pump P-101" — parent breadcrumb
      const parent = this.vm && this.vm.raw && this.vm.raw.parentCml;
      if (!parent) return 'Measurement point';
      const parts = ['Point on'];
      if (parent.cmlNo) parts.push(parent.cmlNo);
      const mt = parent.masterTag && (parent.masterTag.name || parent.masterTag.tagNo);
      if (mt) parts.push(`· ${mt}`);
      return parts.join(' ');
    },
    tmlLatestReadingValue() {
      const v = this.vm && this.vm.raw && this.vm.raw.latestReadingValue;
      if (v == null || Number.isNaN(Number(v))) return '—';
      return Number(v).toFixed(2);
    },
    tmlLastReadingDateDisplay() {
      const iso = this.vm && this.vm.raw && this.vm.raw.latestReadingDate;
      if (!iso) return 'No readings yet';
      return this._relativeTime(iso) || this._formatDateShort(iso) || '—';
    },
    tmlLastReadingDateAux() {
      const iso = this.vm && this.vm.raw && this.vm.raw.latestReadingDate;
      if (!iso) return null;
      return this._formatDateShort(iso);
    },
    tmlParentCmlLabel() {
      const parent = this.vm && this.vm.raw && this.vm.raw.parentCml;
      if (!parent) return '—';
      return parent.cmlNo || parent.name || '—';
    },
    tmlParentMasterTagName() {
      const parent = this.vm && this.vm.raw && this.vm.raw.parentCml;
      if (!parent || !parent.masterTag) return null;
      return parent.masterTag.name || parent.masterTag.tagNo || null;
    },
    tmlForgeDbIdDisplay() {
      const dbId = this.vm && this.vm.raw && this.vm.raw.forgeElementDbId;
      if (dbId == null) return null;
      return `dbId ${dbId}`;
    },
  },

  watch: {
    isOpen(newVal) {
      if (newVal) this.$nextTick(this._installCloseListeners);
      else this._removeCloseListeners();
    },
  },

  beforeDestroy() {
    this._removeCloseListeners();
  },

  methods: {
    onRetry() {
      debugLog('[CmlPopupCard] retry clicked');
      retryPopupCard();
    },
    onViewDetail() {
      // T018 (US1) wires the actual route-push. For T009 we emit so a parent
      // can wire externally if needed; the singleton mount also passes through.
      debugLog('[CmlPopupCard] View Detail clicked', this.entityId);
      this.$emit('view-detail', { entityType: this.entityType, entityId: this.entityId, vm: this.vm });
    },
    onCreateTask(payload) {
      debugLog('[CmlPopupCard] create-task forwarded', payload && payload.boardId);
      this.$emit(POPUP_ACTION.CREATE_TASK, payload);
    },
    onLinkTask(payload) {
      debugLog('[CmlPopupCard] link-task forwarded', this.entityId);
      this.$emit(POPUP_ACTION.LINK_TASK, payload);
    },

    // ── View-model helpers ────────────────────────────────────────────────
    _statusValue() {
      // Source of truth is the raw conditionRating enum. The popup endpoint
      // `/api/v1/cmls/{id}` returns this field as an int (0..4) while the
      // monitoring-tab list endpoint returns the PascalCase string —
      // statusLabelForCml only knows strings, so without this normalization
      // an int 3 becomes the literal "3" in the UI and the status color falls
      // back to gray. Map int → enum string here so every consumer sees the
      // same shape regardless of which endpoint produced the data.
      const raw = this.vm && this.vm.raw;
      if (!raw) return null;
      const v = raw.conditionRating != null ? raw.conditionRating : raw.status;
      if (v == null) return null;
      const intMap = {
        0: 'Acceptable', 1: 'Monitor', 2: 'ReInspect',
        3: 'FfsRequired', 4: 'RepairRequired',
      };
      if (typeof v === 'number') return intMap[v] || null;
      return String(v);
    },
    _relativeTime(iso, opts) {
      // Returns "3 months ago", "in 5 months", "today", etc. Short, plain
      // English; falls back to the absolute date when the diff is unintuitive
      // (e.g. > 5 years away).
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      const ms = d.getTime() - Date.now();
      const past = ms < 0;
      const abs = Math.abs(ms);
      const day = 86400000;
      const days = Math.round(abs / day);
      const months = Math.round(abs / (day * 30.44));
      const years = Math.round(abs / (day * 365.25));
      const tense = past ? (n, unit) => `${n} ${unit} ago` : (n, unit) => `in ${n} ${unit}`;
      if (days < 1) return past ? 'today' : 'today';
      if (days < 30) return tense(days, days === 1 ? 'day' : 'days');
      if (months < 12) return tense(months, months === 1 ? 'month' : 'months');
      if (years <= 5) return tense(years, years === 1 ? 'year' : 'years');
      return this._formatDateShort(iso);
    },
    _formatDateShort(iso) {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
    },

    // ── Close triggers (FR-014) ────────────────────────────────────────────
    _installCloseListeners() {
      this.outsideHandler = (event) => {
        const host = this.$refs.hostEl;
        if (!host) return;
        if (host.contains(event.target)) return;

        // Don't close if the click landed on a CML/TML marker — the marker
        // click handler will switch popups (or toggle the current one) on
        // its own. Without this guard, every marker click triggers an
        // outside-close THEN a marker-open, causing visible flicker AND
        // breaking "click same marker to dismiss" (close→reopen loop), which
        // is the user-reported "first time works, then needs 2 clicks" bug.
        const forgeService = this.$store?.state?.forgeService;
        if (forgeService
          && (forgeService._findCmlAtClient?.(event.clientX, event.clientY)
              || forgeService._findTmlAtClient?.(event.clientX, event.clientY))) {
          debugLog('[CmlPopupCard] outside click on marker → defer to marker handler');
          return;
        }

        // 360 viewer: a click on a CML/TML hotspot pill should switch/toggle the
        // popup, not close it. The pano hotspot's own clickHandlerFunc
        // (PanoramaHotspotManager) calls openPopupCard; defer here so we don't
        // close-then-reopen (flicker) and so clicking the same hotspot toggles
        // it closed — parity with the 3D marker path above.
        if (event.target?.closest?.('.cml-360-hotspot, .tml-360-hotspot')) {
          debugLog('[CmlPopupCard] outside click on 360 hotspot → defer to hotspot handler');
          return;
        }

        // AVA-437: clicks inside a popup-OWNED overlay (document menu, add-task
        // chooser) are not "outside" — Vuetify teleports them to [data-app], so
        // defer here to honor the stay-open policy (D6). Owned overlays set
        // content-class="cml-popup-owned-overlay".
        if (event.target?.closest?.('.cml-popup-owned-overlay')) {
          debugLog('[CmlPopupCard] outside click on owned overlay → defer');
          return;
        }

        debugLog('[CmlPopupCard] click outside → close');
        closePopupCard();
      };
      this.escHandler = (event) => {
        if (event.key === 'Escape') {
          debugLog('[CmlPopupCard] Esc → close');
          closePopupCard();
        }
      };
      // Listen on `click` (capture) — NOT `mousedown`.
      //
      // Why click, not mousedown:
      //   - The CML/TML marker click handlers in forgeService also fire on
      //     `click`. When the close fires on mousedown but the marker handlers
      //     fire on click, they desync — for a slow click (button held), the
      //     user sees the popup close immediately at mousedown, then a
      //     leftover click event arrives at mouseup with stale-looking logs.
      //     Switching to click keeps both in the same event tick.
      //   - The "defer to marker handler" guard above still works correctly:
      //     capture-phase order is document → viewer.container, so our
      //     document-level click runs BEFORE the marker click handler. If
      //     we detect a marker hit, we defer (return early) and the marker
      //     handler runs next in the same event, opening the new popup.
      //   - Closing on mouseup matches conventional web popup UX (menus,
      //     tooltips, dropdowns all close on click, not mousedown).
      //
      // Capture-phase is preserved so we still get the event before any
      // descendant element's click listeners (in particular the marker
      // click handlers attached to viewer.container).
      document.addEventListener('click', this.outsideHandler, true);
      document.addEventListener('keydown', this.escHandler);
    },
    _removeCloseListeners() {
      if (this.outsideHandler) {
        document.removeEventListener('click', this.outsideHandler, true);
        this.outsideHandler = null;
      }
      if (this.escHandler) {
        document.removeEventListener('keydown', this.escHandler);
        this.escHandler = null;
      }
    },
  },
};
</script>
